import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Text,
  SimpleGrid,
  Stack,
  Badge,
  useColorModeValue,
  Icon,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useUserId } from '@nhost/react';
import { useGetRestaurantByUserIdQuery } from 'src/utils/__generated__/graphql';
import { LatLngExpression } from 'leaflet';
import { MapContainer, TileLayer, Marker, Circle, Popup } from 'react-leaflet';
import { MapIcon } from './shared/MapIcon';
import { nhost } from 'src/lib/nhost';
import { Link } from 'react-router-dom';
import { FiEdit2, FiMapPin } from 'react-icons/fi';
import { PageHeader, ModernCard, LoadingSpinner } from './shared';
import { ElementType } from 'react';

export const Restaurant = () => {
  const toast = useToast();
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const userId = useUserId();

  const { data, loading, error } = useGetRestaurantByUserIdQuery({
    variables: { userId },
  });

  const restaurant = data?.restaurant?.[0];

  // Fix: Ensure coordinates are [lat, lng] for Leaflet
  const coordinates = restaurant?.location?.coordinates;
  const restaurantCoordinates: LatLngExpression =
    coordinates && coordinates.length === 2
      ? [coordinates[1], coordinates[0]] // [lat, lng]
      : [0, 0];

  const logoFileId = restaurant?.image ?? '';
  const coverFileId = restaurant?.cover ?? '';

  const logoUrl = nhost.storage.getPublicUrl({ fileId: logoFileId });
  const coverUrl = nhost.storage.getPublicUrl({ fileId: coverFileId });

  if (loading) return <LoadingSpinner text="Loading restaurant details..." />;
  if (error) {
    toast({
      title: 'Error',
      description: error.message,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return (
      <ModernCard p={8}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error Loading Restaurant
          </Text>
          <Text color={textColor}>{error.message}</Text>
        </VStack>
      </ModernCard>
    );
  }

  return (
    <>
      <PageHeader
        title={restaurant?.name || 'Restaurant Details'}
        subtitle={restaurant?.short_description || 'Manage your restaurant information'}
        badge={
          <Badge colorScheme={restaurant?.isActive ? 'green' : 'red'} fontSize="sm" borderRadius="full" px={3} py={1}>
            {restaurant?.isActive ? 'Active' : 'Inactive'}
          </Badge>
        }
        actions={
          <Link to="/restaurant/edit">
            <Button
              leftIcon={<Icon as={FiEdit2 as ElementType} />}
              colorScheme="brand"
              variant="outline"
              size={{ base: 'md', md: 'lg' }}
              _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Edit Restaurant</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Edit</Text>
            </Button>
          </Link>
        }
      />

      {/* Hero Section */}
      <Box position="relative" height={{ base: '200px', md: '300px' }} borderRadius="xl" mb={{ base: '16', md: '24' }}>
        <Image src={coverUrl} alt={restaurant?.name} w="full" h="full" objectFit="cover" />
        <Box
          position="absolute"
          bottom={{ base: '-8', md: '-16' }}
          left="50%"
          transform="translateX(-50%)"
          width={{ base: '95%', md: '80%' }}
        >
          <ModernCard p={4}>
            <Flex gap={{ base: 4, md: 6 }} direction={{ base: 'column', md: 'row' }} align="center">
              <Image
                src={logoUrl}
                alt={restaurant?.name}
                width={{ base: '100px', md: '150px' }}
                height={{ base: '100px', md: '150px' }}
                objectFit="cover"
                borderRadius="xl"
                boxShadow="lg"
              />
              <Stack spacing={2} textAlign={{ base: 'center', md: 'left' }}>
                <Heading size={{ base: 'md', md: 'lg' }}>{restaurant?.name}</Heading>
                <Text color={textColor} fontSize={{ base: 'md', md: 'lg' }}>
                  {restaurant?.short_description}
                </Text>
                <Badge
                  colorScheme={restaurant?.isActive ? 'green' : 'red'}
                  width="fit-content"
                  alignSelf={{ base: 'center', md: 'flex-start' }}
                >
                  {restaurant?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Stack>
            </Flex>
          </ModernCard>
        </Box>
      </Box>

      {/* Details Section */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 6, md: 8 }} mt={{ base: 6, md: 8 }}>
        <ModernCard p={4}>
          <VStack spacing={6} align="stretch">
            <Heading size="md" mb={2}>
              Basic Information
            </Heading>
            <Stack spacing={4}>
              <DetailItem label="Address" value={restaurant?.address} />
              <DetailItem label="Phone" value={restaurant?.phone} />
              <DetailItem label="Email" value={restaurant?.email} />
              <DetailItem label="Type" value={restaurant?.type} />
              <DetailItem label="Specialty" value={restaurant?.specialty?.name} />
              <DetailItem label="Description" value={restaurant?.description} />
            </Stack>
          </VStack>
        </ModernCard>

        <ModernCard p={4}>
          <VStack spacing={6} align="stretch">
            <Heading size="md" mb={2}>
              Business Details
            </Heading>
            <VStack spacing={4} align="stretch">
              <DetailItem label="Delivery Charge" value={`₹${restaurant?.delivery_charge}`} />
              <DetailItem label="Admin Commission" value={`${restaurant?.admin_commission}%`} />
              <DetailItem label="Delivery Commission" value={`${restaurant?.delivery_commission}%`} />
              <DetailItem label="Minimum Order" value={`₹${restaurant?.min_order_amount}`} />
              <DetailItem label="Threshold Amount" value={`₹${restaurant?.threshold_amount}`} />
              <DetailItem label="Free Delivery Distance" value={`${restaurant?.free_distance} meters`} />
              <DetailItem label="Maximum Delivery Distance" value={`${restaurant?.max_distance} meters`} />
            </VStack>
          </VStack>
        </ModernCard>
      </SimpleGrid>

      <ModernCard p={4} mt={4}>
        {restaurant?.carriers?.map((carrier) => {
          const carrierUrl = nhost.storage.getPublicUrl({ fileId: carrier?.image ?? '' });

          return (
            <Flex
              key={carrier?.id}
              gap={{ base: 4, md: 6 }}
              direction={{ base: 'column', md: 'row' }}
              align="center"
              mb={4}
            >
              <Image
                src={carrierUrl}
                alt={carrier?.name}
                width={{ base: '100px', md: '150px' }}
                height={{ base: '100px', md: '150px' }}
                objectFit="cover"
                borderRadius="xl"
                boxShadow="lg"
              />
              <Stack spacing={1} textAlign={{ base: 'center', md: 'left' }}>
                <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>
                  Delivery Partner
                </Text>
                <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                  {carrier?.name}
                </Text>
                <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>
                  {carrier?.verification_status}
                </Text>
                <Badge
                  colorScheme={carrier?.isOnline ? 'green' : 'red'}
                  width="fit-content"
                  alignSelf={{ base: 'center', md: 'flex-start' }}
                >
                  {carrier?.isOnline ? 'Online' : 'Offline'}
                </Badge>
                <Badge
                  colorScheme={carrier?.verification_status === 'verified' ? 'green' : 'red'}
                  width="fit-content"
                  alignSelf={{ base: 'center', md: 'flex-start' }}
                >
                  {carrier?.verification_status === 'verified' ? 'Verified' : 'Unverified'}
                </Badge>
              </Stack>
            </Flex>
          );
        })}
      </ModernCard>

      {/* Map Section */}
      <ModernCard mt={{ base: 6, md: 8 }} p={4}>
        <VStack spacing={6} align="stretch">
          <Heading size="md" mb={2}>
            Location
          </Heading>
          <Box borderRadius="xl" overflow="hidden" height={{ base: '500px', md: '800px' }}>
            {/* Only render MapContainer if coordinates are valid */}
            {coordinates && coordinates.length === 2 ? (
              <MapContainer
                center={restaurantCoordinates}
                zoom={14}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                />
                <Marker position={restaurantCoordinates} icon={MapIcon}>
                  <Popup>Restaurant Location</Popup>
                </Marker>
                <Circle
                  center={restaurantCoordinates}
                  radius={Number(
                    typeof restaurant?.free_distance === 'number' && !isNaN(restaurant?.free_distance)
                      ? restaurant?.free_distance
                      : 0
                  )}
                  color="teal"
                  fillOpacity={0.1}
                />
                <Circle
                  center={restaurantCoordinates}
                  radius={Number(
                    typeof restaurant?.max_distance === 'number' && !isNaN(restaurant?.max_distance)
                      ? restaurant?.max_distance
                      : 0
                  )}
                  color="red"
                  fillOpacity={0.1}
                  weight={1}
                />
              </MapContainer>
            ) : (
              <VStack spacing={4} py={8} textAlign="center">
                <Icon as={FiMapPin as ElementType} boxSize={12} color={textColor} opacity={0.5} />
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  No Location Data
                </Text>
                <Text color={textColor} opacity={0.8}>
                  Restaurant location coordinates are not available.
                </Text>
              </VStack>
            )}
          </Box>
        </VStack>
      </ModernCard>
    </>
  );
};

// Helper component for detail items
const DetailItem = ({ label, value }: { label: string; value: string | undefined | null }) => (
  <Flex justify="space-between" align="center">
    <Text fontWeight="medium" color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
      {label}:
    </Text>
    <Text fontSize={{ base: 'sm', md: 'md' }}>{value || '-'}</Text>
  </Flex>
);

// Edit icon component
// const EditIcon = () => (
//   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
//       stroke="currentColor"
//       strokeWidth="2"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//     <path
//       d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.43741 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z"
//       stroke="currentColor"
//       strokeWidth="2"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//   </svg>
// );
